import defaultAttributes from "backend/defaultAttributes";
import useComments from "backend/hooks/comments/useComments";
import useElement from "backend/hooks/elements/useElement";
import useYearsLastPeriod from "backend/hooks/model/useYearsLastPeriod";
import { ICardElementWithChildren } from "backend/model/model";
import getValues from "backend/values";
import PerformanceTrend from "components/PerformanceTrend";
import ValueHistory from "components/ValueHistory";
import KPIToolbar from "components/toolbars/KPIToolbar";
import AnalysisCard from "design/components/cards/AnalysisCard/AnalysisCard";
import {
	KPIPerformanceCard,
	YearEndTargetCard,
} from "design/components/cards/KPIPerformanceCard";
import MeasureInfo from "design/components/cards/MeasureInfo";
import Todo from "design/components/misc/Todo";
import InitiativeHeading from "design/components/texts/InitiativeHeading";
import SplitView from "design/layouts/SplitView";
import StackView from "design/layouts/StackView";
import Page from "design/pages/Page";
import { resultSort } from "init/sort";
import { IChartSettings } from "qpr-base/configuration/types";
import { useAppContext } from "qpr-base/context/AppCtxProvider";
import useLink from "qpr-base/hooks/useLink";
import useQueryBuilder from "qpr-base/querybuilder/useQueryBuilder";
import Anchor from "qpr-base/ui/Anchor";
import React from "react";
import { useQuery } from "react-query";

const CHARTSETTINGS: IChartSettings = {
	series: [
		{ symbol: "TAR", color: "rgb(93,98,104)" },
		{
			symbol: "ACT",
			color: "rgba(38, 166, 242, 0.8)",
		},
	],
};
const KPIDetails = () => {
	const ctx = useAppContext();
	const link = useLink();
	const { data: comments } = useComments({
		id: ctx.elementId || "",
		period: ctx.period || "",
	});
	const { data: kpi, isLoading } = useElement(ctx.elementId);
	const { data: scorecardKpis, isLoading: isScorecardKpisLoading } =
		useQueryBuilder<ICardElementWithChildren[]>((q) =>
			q(ctx.elementId)
				.scorecard()
				.attributes(...defaultAttributes(ctx.period, []))
				.measures({ criteria: { typename: "Initiative" } })
		);

	const sortedScorecardKPIs = React.useMemo(
		() => resultSort(scorecardKpis?.data || []),
		[scorecardKpis]
	);

	const lastPeriod = useYearsLastPeriod(ctx.period || "");

	const { data: lastPeriodValue } = useQuery(
		`last-period-for-${ctx.elementId}-${ctx.period}-${lastPeriod?.name}`,
		async () => {
			return getValues(ctx.elementId || "", [lastPeriod!], true, []);
		},
		{ enabled: !!lastPeriod }
	);

	const [firstComment] = comments || [];

	if (!kpi) {
		return null;
	}

	return (
		<Page
			isLoading={
				isLoading || isScorecardKpisLoading || !scorecardKpis?.data || !kpi
			}
			Toolbar={KPIToolbar}
		>
			<SplitView left="150px" right="1100px" gap="20px">
				<div>
					{/*
          <LeftMenuPosition>
            <ItemDrawer
              rtl={ctx.rtl}
              title="KPIs"
              items={scorecardKpis?.data!}
              onSelect={(item) => {
                link({ meaId: item.id });
              }}
            />
            <PageContentMenu
              componentData={{
                items: [
                  { title: t("KPI Information"), anchor: "header" },
                  { title: t("Performance"), anchor: "performance" },
                  { title: t("Justification"), anchor: "justification" },
                  { title: t("Value data"), anchor: "valuedata" },
                ],
              }}
            />
          </LeftMenuPosition>
            */}
				</div>
				<div>
					<StackView>
						<Anchor name="header" />
						<MeasureInfo
							title="KPI Information"
							items={[
								{
									label: "Description",
									text: kpi!.description || "",
								},
								{
									label: "Measurement Unit",
									text: kpi!.ACT_unitname || "",
								},
								{
									label: "Strategic Objective",
									text:
										(kpi?.parentTypename || "") === "Strategic Objective"
											? (kpi?.parentName || "") + "\n" + kpi?.parentDescription
											: "",
								},
								{
									label: "Measurement Frequency",
									text: kpi!.periodlevelname || "",
								},
							]}
						/>

						<SplitView left="30%" right="70%" gap="30px">
							<div>
								<InitiativeHeading>Performance</InitiativeHeading>

								<KPIPerformanceCard
									period={kpi.periodname || ""}
									element={kpi}
									settings={{
										title: "Target Achievement",
										fields: [
											{ attribute: "ACT_YTD_prettyvalue" },
											{ attribute: "TAR_YTD_prettyvalue" },
										],
									}}
								/>
								{lastPeriodValue && lastPeriodValue[0] ? (
									<YearEndTargetCard
										element={lastPeriodValue[0]}
										settings={{ value: { value: "10" } }}
									/>
								) : null}
							</div>
							<div>
								<Anchor name="performance" />
								<InitiativeHeading>Performance Trend</InitiativeHeading>
								<PerformanceTrend element={kpi!} settings={CHARTSETTINGS} />
							</div>
						</SplitView>

						<Anchor name="justification" />
						<InitiativeHeading subTitle={ctx.period}>
							Justifications & Recommendations
						</InitiativeHeading>

						<Todo>
							<pre>{JSON.stringify(comments, null, 2)}</pre>
						</Todo>

						{firstComment ? (
							<AnalysisCard
								userData={{
									name: firstComment.modifiedby || firstComment.createdby,
									subtitle: "Owner",
								}}
								date={new Date(
									firstComment.lastmodifieddate
								).toLocaleDateString()}
								description={
									firstComment ? firstComment.description : "No Comments yet"
								}
							/>
						) : (
							<AnalysisCard description="No Comments Yet" />
						)}

						<Anchor name="valuedata" />
						<InitiativeHeading>KPI Value Data</InitiativeHeading>
						<ValueHistory element={kpi!} />
						<div style={{ height: "500px" }} />
					</StackView>
				</div>
			</SplitView>
		</Page>
	);
};

export default KPIDetails;
